/**
 * @file        src/pages/flight/ReadOnlyListPage.jsx
 * @description Config-driven read-only, paginated list over an existing data
 *              endpoint. Used by the B2C Flight operational screens (bookings,
 *              search history, used coupons, cancellations).
 * @author      Kinetika Labs
 * @created     2026-07-01
 * @changelog
 *   - 2026-07-01 Claude — Initial (B2C Flight Management).
 *   - 2026-07-08 Claude — Added optional `showIndex` prop (default true) to hide
 *                          the leading "#" serial column on lists that don't want
 *                          it (e.g. the flight Booking List).
 *   - 2026-07-09 Claude — Added optional `footNote` prop: a small legend/help
 *                          line under the table (e.g. the Cancellation List's
 *                          "C:- Cancel, B:- Booking …" key, matching the ref).
 *   - 2026-08-04 Claude — Optional `dense` prop (default false): tighter row
 *                          padding + a bounded-height, sticky-thead scroll
 *                          container (`.table.dense` / `.table-wrap.scroll` in
 *                          admin.css) for the flight/hotel Booking List (CR
 *                          D3). A column may set `align:'right'` to right-align
 *                          a numeric cell (e.g. Fare). Both are opt-in and
 *                          leave every other ReadOnlyListPage consumer as-is.
 *   - 2026-09-15 Claude — New optional `notice` prop: a one-line informational bar
 *     between the head and the table, same shape and styling as
 *     SimpleLookupPage's. Read-only money screens (Credit Dues) need to state the
 *     rule behind the numbers just as much as the config screens do.
 *   - 2026-09-25 Claude — `keepPreviousData: true` is a React Query **v4** option
 *                          and this workspace is on v5 (5.101), where no such
 *                          option exists and it was silently ignored. So every
 *                          page / filter / search change dropped `data` to
 *                          undefined, the table was swapped for six skeleton rows
 *                          and the panel collapsed ~700px before growing back —
 *                          the "pages flicker / the page moves" report. v5 spells
 *                          it `placeholderData: keepPreviousData` (a FUNCTION
 *                          imported from the package, not a boolean).
 */
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Pager from '../../components/Pager';
import TableSkeleton from '../../components/TableSkeleton';
import { get } from '../../api/client';

export function fmtDate(s) {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return String(s);
  return d.toLocaleString('en-IN', {
    hour: '2-digit', minute: '2-digit', hour12: true,
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

/**
 * @param {object}  props
 * @param {string}  props.title
 * @param {string}  props.subtitle
 * @param {string}  props.endpoint    e.g. '/flight/bookings'
 * @param {string}  props.queryRoot   cache key root
 * @param {Array}   props.columns     [{ key, label, render?(value,row) }]
 * @param {boolean} [props.searchable=true]
 * @param {string}  [props.searchLabel='Search']
 * @param {boolean} [props.dense=false] Tighter rows + sticky, scroll-bounded thead.
 * @param {node}    [props.notice]   one-line informational bar above the table.
 */
export default function ReadOnlyListPage({
  title, subtitle, endpoint, queryRoot, columns, searchable = true, searchLabel = 'Search',
  rowActions, actionsLabel = 'Actions', showIndex = true, extraParams, toolbar, footNote,
  dense = false, notice,
}) {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(30);
  // ?q=<term> arrives pre-applied. Every screen built on this component is
  // reached by a link from somewhere else — the Action Centre sends a booking
  // reference here — and a link that lands on an unfiltered list with the
  // reference still to paste is a hint, not a link. Read ONCE, into ordinary
  // state: the box stays the admin's after that, so clearing it is not fought
  // by the URL.
  const [urlParams] = useSearchParams();
  const [initialQ] = useState(() => urlParams.get('q') || '');
  const [search, setSearch] = useState(initialQ);
  const [applied, setApplied] = useState(initialQ);

  const params = { page, pageSize, ...(extraParams || {}) };
  if (applied) params.q = applied;

  const { data, isLoading } = useQuery({
    queryKey: [queryRoot, 'list', params],
    queryFn: () => get(endpoint, { params }),
    placeholderData: keepPreviousData,
  });

  const rows = data?.data || [];
  const total = data?.meta?.total || 0;

  return (
    <>
      <PageHead title={title} subtitle={subtitle} />

      {notice ? <div className="notice-banner info">{notice}</div> : null}

      {toolbar ? <div className="list-toolbar">{toolbar}</div> : null}

      <article className="panel glass">
        {searchable ? (
          <form className="search-row" onSubmit={(e) => { e.preventDefault(); setPage(1); setApplied(search); }}>
            <div className="field">
              <label>{searchLabel}</label>
              <input placeholder="Type to search…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <button className="btn primary" type="submit"><Icon name="search" /> Search</button>
          </form>
        ) : null}

        <div className={`table-wrap${dense ? ' scroll' : ''}`}>
          {isLoading ? <TableSkeleton /> : (
            <table className={`table${dense ? ' dense' : ''}`}>
              <thead>
                <tr>
                  {showIndex ? <th>#</th> : null}
                  {columns.map((c) => <th key={c.key} className={c.align === 'right' ? 'num' : undefined}>{c.label}</th>)}
                  {rowActions ? <th>{actionsLabel}</th> : null}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={columns.length + (showIndex ? 1 : 0) + (rowActions ? 1 : 0)} className="empty">No records found.</td></tr>
                ) : rows.map((row, idx) => (
                  <tr key={row.id ?? idx}>
                    {showIndex ? <td className="mono">{(page - 1) * pageSize + idx + 1}</td> : null}
                    {columns.map((c) => (
                      <td key={c.key} className={c.align === 'right' ? 'num' : undefined}>{c.render ? c.render(row[c.key], row) : (row[c.key] ?? <span className="muted">—</span>)}</td>
                    ))}
                    {rowActions ? <td>{rowActions(row)}</td> : null}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {footNote ? <p className="list-legend">{footNote}</p> : null}

        <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} />
      </article>
    </>
  );
}
